import * as client from "../../../client";
import {
  EffectiveExcerpt,
  effectiveExcerpts,
  removedExcerpts,
} from "./excerpts";

/**
 * Where a limit / sublimit / deductible row comes from, and so where an edit
 * to it lives:
 *
 *   - `extracted`: read from the selected form, untouched.
 *   - `edited`: a form excerpt whose value the user replaced. The override is
 *     stored on the form's entry and can be reverted.
 *   - `added`: typed in by the user under a peril the form already covers.
 *     Also stored on the form's entry.
 *   - `manual`: lives in the manual section, independent of any form, and
 *     shows whichever form is selected.
 */
export type Provenance = "extracted" | "edited" | "added" | "manual";

export const excerptProvenance = (
  excerpt: EffectiveExcerpt,
  manualPeril: boolean
): Provenance => {
  if (manualPeril) return "manual";
  if (!excerpt.isOverride) return "extracted";
  return excerpt.original === null ? "added" : "edited";
};

export const sublimitProvenance = (
  limit: client.Sublimit,
  manual: boolean
): Provenance => {
  if (manual) return "manual";
  return limit.manual_override != null ? "edited" : "extracted";
};

/** Edits stored on one form, for one tab or summed across the three. */
export type DocumentEditCounts = {
  edited: number;
  added: number;
  removed: number;
};

export type EditSection = "limits" | "sublimits" | "deductibles";

export const totalEdits = (counts: DocumentEditCounts): number =>
  counts.edited + counts.added + counts.removed;

type ExcerptEntry = client.AdvancedLimit | client.AdvancedDeductible;

/** Count the edits visible on a form's peril entries. A peril the manual
 *  section shadows is skipped: its form edits are not shown. */
const perilEditCounts = (
  extracted: Record<string, ExcerptEntry>,
  manual: Record<string, ExcerptEntry>
): DocumentEditCounts => {
  const counts: DocumentEditCounts = { edited: 0, added: 0, removed: 0 };
  for (const [peril, entry] of Object.entries(extracted)) {
    if (manual[peril] !== undefined) continue;
    for (const excerpt of effectiveExcerpts(entry)) {
      const provenance = excerptProvenance(excerpt, false);
      if (provenance === "edited") counts.edited += 1;
      if (provenance === "added") counts.added += 1;
    }
    counts.removed += removedExcerpts(entry).length;
  }
  return counts;
};

/** Edits stored on one form within one tab. */
export const sectionEditCounts = (
  section: EditSection,
  policyName: string,
  sublimitInfo: client.SublimitInfo | null | undefined,
  deductibleInfo: client.AdvancedDeductibleInfo | null | undefined,
  limitInfo: client.AdvancedLimitInfo | null | undefined
): DocumentEditCounts => {
  switch (section) {
    case "limits":
      return perilEditCounts(
        limitInfo?.extracted_limits[policyName] ?? {},
        limitInfo?.manual_limits ?? {}
      );
    case "deductibles":
      return perilEditCounts(
        deductibleInfo?.extracted_deductibles[policyName] ?? {},
        deductibleInfo?.manual_deductibles ?? {}
      );
    case "sublimits": {
      const counts: DocumentEditCounts = { edited: 0, added: 0, removed: 0 };
      for (const limit of sublimitInfo?.extracted_sublimits[policyName] ?? []) {
        if (limit.is_removed) counts.removed += 1;
        else if (sublimitProvenance(limit, false) === "edited")
          counts.edited += 1;
      }
      return counts;
    }
  }
};

const SECTIONS: EditSection[] = ["limits", "sublimits", "deductibles"];

/** Edits stored on one form across all three tabs. */
export const documentEditCounts = (
  policyName: string,
  sublimitInfo: client.SublimitInfo | null | undefined,
  deductibleInfo: client.AdvancedDeductibleInfo | null | undefined,
  limitInfo: client.AdvancedLimitInfo | null | undefined
): DocumentEditCounts =>
  SECTIONS.map((section) =>
    sectionEditCounts(
      section,
      policyName,
      sublimitInfo,
      deductibleInfo,
      limitInfo
    )
  ).reduce((sum, counts) => ({
    edited: sum.edited + counts.edited,
    added: sum.added + counts.added,
    removed: sum.removed + counts.removed,
  }));

const manualExcerptCount = (entries: Record<string, ExcerptEntry>): number =>
  Object.values(entries).reduce(
    (sum, entry) => sum + effectiveExcerpts(entry).length,
    0
  );

/** Rows in one tab's manual section, which show with every form. */
export const sectionManualCount = (
  section: EditSection,
  sublimitInfo: client.SublimitInfo | null | undefined,
  deductibleInfo: client.AdvancedDeductibleInfo | null | undefined,
  limitInfo: client.AdvancedLimitInfo | null | undefined
): number => {
  switch (section) {
    case "limits":
      return manualExcerptCount(limitInfo?.manual_limits ?? {});
    case "deductibles":
      return manualExcerptCount(deductibleInfo?.manual_deductibles ?? {});
    case "sublimits":
      return sublimitInfo?.manual_sublimits.length ?? 0;
  }
};

/** Rows in the manual section across all three tabs. */
export const manualEntryCount = (
  sublimitInfo: client.SublimitInfo | null | undefined,
  deductibleInfo: client.AdvancedDeductibleInfo | null | undefined,
  limitInfo: client.AdvancedLimitInfo | null | undefined
): number =>
  SECTIONS.reduce(
    (sum, section) =>
      sum +
      sectionManualCount(section, sublimitInfo, deductibleInfo, limitInfo),
    0
  );
